/**
 * Registry of marketing page components, keyed by a stable `MarketingPageId`.
 *
 * `marketingRoutes.tsx` used to statically import every marketing page component into the route
 * manifest, and that manifest is imported by the browser entry (`entry-client-marketing.tsx`) — so
 * the homepage downloaded every marketing page's code (business signup, blog widgets, AI page
 * screenshots, …). This file is the seam that lets each page ship its own chunk instead:
 *
 *   - The client (`entry-client-marketing.tsx`) only ever calls `preloadMarketingPage`/
 *     `lazyMarketingPage` for the page it actually needs, which dynamic-imports the real module.
 *   - The server (`entry-server.tsx`) statically imports every real component (bundle size doesn't
 *     matter server-side) and calls `registerMarketingPages` once at module load, so SSR — and the
 *     client's first hydration render — always resolve every page synchronously, never through
 *     `React.lazy`/`Suspense`.
 *   - The backend (`graphql/helpers/marketingSsrHandler.ts`) reads `PAGE_MODULE_IDS` (re-exported
 *     from `entry-server.tsx` as `marketingPageModuleIds`) to look a route's chunk up in the Vite
 *     manifest and emit its CSS/modulepreload tags in the SSR HTML.
 *
 * Imported by both the client and server bundles — never by the backend directly (the backend only
 * ever sees the built Vite manifest + the `PAGE_MODULE_IDS` string values baked into the SSR bundle).
 */

import { ComponentType, lazy, LazyExoticComponent } from "react";

// The single source of truth for the id set — `MarketingPageId` is derived from it (rather than
// hand-written alongside it) so a test can iterate every id at runtime without duplicating the
// list a third time, and so `PAGE_LOADERS`/`PAGE_MODULE_IDS` below have something independent to be
// checked against (see `marketingPages.test.ts`).
export const MARKETING_PAGE_IDS = [
	"HomeMarketing",
	"About",
	"Contact",
	"PrivacyStatement",
	"Terms",
	"AiTravelPlannerPage",
	"AssistantTravelPlannerPage",
	"B2CPricingPage",
	"Blog",
	"BlogArticleRoute",
	"BioLinksPage",
	"BudgetPlannerPage",
	"BusinessPage",
	"BusinessHomePage",
	"BusinessTripBuildingPage",
	"BusinessFaqPage",
	"CreatorPage",
	"CreatorsPage",
	"DiscoverHub",
	"DiscoverCountryRoute",
	"DiscoverDestinationRoute",
	"NotFound",
	"EsimPage",
	"FeaturePage",
	"ItineraryPlannerPage",
	"MadMonkeyPage",
	"PackingListPage",
	"PartnerMapPage",
	"PhotobookPage",
	"TravelReelPage",
	"TravelTrackerPage",
] as const;

export type MarketingPageId = (typeof MARKETING_PAGE_IDS)[number];

/**
 * Static-string dynamic imports — one module specifier per page, written out literally so Vite's
 * static analysis can see each one and give it its own chunk. Never build this path dynamically
 * (e.g. `import(\`components/website/pages/${id}\`)`) — that defeats the whole point.
 */
export const PAGE_LOADERS: Record<MarketingPageId, () => Promise<ComponentType<any>>> = {
	HomeMarketing: () => import("components/website/pages/HomeMarketing").then((m) => m.default),
	About: () => import("components/website/pages/About").then((m) => m.default),
	Contact: () => import("components/website/pages/Contact").then((m) => m.default),
	PrivacyStatement: () => import("components/website/pages/PrivacyStatement").then((m) => m.default),
	Terms: () => import("components/website/pages/Terms").then((m) => m.default),
	AiTravelPlannerPage: () => import("components/website/pages/AiTravelPlannerPage").then((m) => m.default),
	AssistantTravelPlannerPage: () => import("components/website/pages/AssistantTravelPlannerPage").then((m) => m.default),
	B2CPricingPage: () => import("components/website/pages/B2CPricingPage").then((m) => m.default),
	Blog: () => import("components/website/pages/Blog").then((m) => m.default),
	BlogArticleRoute: () => import("components/website/pages/BlogArticleRoute").then((m) => m.default),
	BioLinksPage: () => import("components/website/pages/BioLinksPage").then((m) => m.default),
	BudgetPlannerPage: () => import("components/website/pages/BudgetPlannerPage").then((m) => m.default),
	BusinessPage: () => import("components/website/pages/BusinessPage").then((m) => m.default),
	BusinessHomePage: () => import("components/website/content/business/homepage/BusinessHomePage").then((m) => m.default),
	BusinessTripBuildingPage: () => import("components/website/content/business/homepage/BusinessTripBuildingPage").then((m) => m.default),
	BusinessFaqPage: () => import("components/website/content/business/homepage/BusinessFaqPage").then((m) => m.default),
	CreatorPage: () => import("components/website/pages/CreatorPage").then((m) => m.default),
	CreatorsPage: () => import("components/website/pages/CreatorsPage").then((m) => m.default),
	DiscoverHub: () => import("components/website/pages/DiscoverHub").then((m) => m.default),
	DiscoverCountryRoute: () => import("components/discover/guideRoutes").then((m) => m.DiscoverCountryRoute),
	DiscoverDestinationRoute: () => import("components/discover/guideRoutes").then((m) => m.DiscoverDestinationRoute),
	NotFound: () => import("components/website/pages/NotFound").then((m) => m.default),
	EsimPage: () => import("components/website/pages/EsimPage").then((m) => m.default),
	FeaturePage: () => import("components/website/pages/FeaturePage").then((m) => m.default),
	ItineraryPlannerPage: () => import("components/website/pages/ItineraryPlannerPage").then((m) => m.default),
	MadMonkeyPage: () => import("components/website/pages/MadMonkeyPage").then((m) => m.default),
	PackingListPage: () => import("components/website/pages/PackingListPage").then((m) => m.default),
	PartnerMapPage: () => import("components/website/pages/PartnerMapPage").then((m) => m.default),
	PhotobookPage: () => import("components/website/pages/PhotobookPage").then((m) => m.default),
	TravelReelPage: () => import("components/website/pages/TravelReelPage").then((m) => m.default),
	TravelTrackerPage: () => import("components/website/pages/TravelTrackerPage").then((m) => m.default),
};

/**
 * Vite manifest key for each page's module — its path relative to `frontend/`, extension included.
 * Must match the real files on disk exactly (`.tsx` vs `.ts`), and stays in sync with the module
 * specifiers above by construction (see the `marketingPages.test.ts` guard). Read by
 * `graphql/helpers/marketingSsrHandler.ts` via `marketingPageModuleIds` (re-exported from
 * `entry-server.tsx`) to look up a route's chunk + CSS in the built manifest.
 */
export const PAGE_MODULE_IDS: Record<MarketingPageId, string> = {
	HomeMarketing: "src/components/website/pages/HomeMarketing.tsx",
	About: "src/components/website/pages/About.tsx",
	Contact: "src/components/website/pages/Contact.tsx",
	PrivacyStatement: "src/components/website/pages/PrivacyStatement.tsx",
	Terms: "src/components/website/pages/Terms.tsx",
	AiTravelPlannerPage: "src/components/website/pages/AiTravelPlannerPage.tsx",
	AssistantTravelPlannerPage: "src/components/website/pages/AssistantTravelPlannerPage.tsx",
	B2CPricingPage: "src/components/website/pages/B2CPricingPage.tsx",
	Blog: "src/components/website/pages/Blog.tsx",
	BlogArticleRoute: "src/components/website/pages/BlogArticleRoute.tsx",
	BioLinksPage: "src/components/website/pages/BioLinksPage.tsx",
	BudgetPlannerPage: "src/components/website/pages/BudgetPlannerPage.tsx",
	BusinessPage: "src/components/website/pages/BusinessPage.tsx",
	BusinessHomePage: "src/components/website/content/business/homepage/BusinessHomePage.tsx",
	BusinessTripBuildingPage: "src/components/website/content/business/homepage/BusinessTripBuildingPage.tsx",
	BusinessFaqPage: "src/components/website/content/business/homepage/BusinessFaqPage.tsx",
	CreatorPage: "src/components/website/pages/CreatorPage.tsx",
	CreatorsPage: "src/components/website/pages/CreatorsPage.tsx",
	DiscoverHub: "src/components/website/pages/DiscoverHub.tsx",
	DiscoverCountryRoute: "src/components/discover/guideRoutes.tsx",
	DiscoverDestinationRoute: "src/components/discover/guideRoutes.tsx",
	NotFound: "src/components/website/pages/NotFound.tsx",
	EsimPage: "src/components/website/pages/EsimPage.tsx",
	FeaturePage: "src/components/website/pages/FeaturePage.tsx",
	ItineraryPlannerPage: "src/components/website/pages/ItineraryPlannerPage.tsx",
	MadMonkeyPage: "src/components/website/pages/MadMonkeyPage.tsx",
	PackingListPage: "src/components/website/pages/PackingListPage.tsx",
	PartnerMapPage: "src/components/website/pages/PartnerMapPage.tsx",
	PhotobookPage: "src/components/website/pages/PhotobookPage.tsx",
	TravelReelPage: "src/components/website/pages/TravelReelPage.tsx",
	TravelTrackerPage: "src/components/website/pages/TravelTrackerPage.tsx",
};

// Module-level resolved cache — populated synchronously by `registerMarketingPages` on the server
// (every page, at module load) and lazily by `preloadMarketingPage` on the client (only the pages
// actually visited). Read by `getResolvedMarketingPage`, which is what lets `MarketingShell` render
// a page synchronously instead of through `React.lazy` whenever it already has the answer.
const resolvedPages = new Map<MarketingPageId, ComponentType<any>>();
// In-flight load promises, so two overlapping calls for the same page (e.g. a hover-preload
// racing the click-through navigation) share one dynamic import instead of firing two.
const inflightLoads = new Map<MarketingPageId, Promise<ComponentType<any>>>();
const lazyPages = new Map<MarketingPageId, LazyExoticComponent<ComponentType<any>>>();

/** Registers already-resolved components — how `entry-server.tsx` seeds every page for SSR. */
export function registerMarketingPages(map: Partial<Record<MarketingPageId, ComponentType<any>>>): void {
	for (const id of Object.keys(map) as MarketingPageId[]) {
		const component = map[id];
		if (component) resolvedPages.set(id, component);
	}
}

/** The component for `id` if it has already resolved (SSR-registered, or a prior client preload). */
export function getResolvedMarketingPage(id: MarketingPageId): ComponentType<any> | undefined {
	return resolvedPages.get(id);
}

/**
 * Resolves `id`'s component, dynamic-importing it if necessary. Idempotent: a resolved page
 * short-circuits, and a load already in flight is shared rather than re-triggered — so calling
 * this from both a hover-prefetch and `lazyMarketingPage`'s fallback for the same id is safe.
 */
export function preloadMarketingPage(id: MarketingPageId): Promise<ComponentType<any>> {
	const cached = resolvedPages.get(id);
	if (cached) return Promise.resolve(cached);

	const inflight = inflightLoads.get(id);
	if (inflight) return inflight;

	const promise = PAGE_LOADERS[id]().then((component) => {
		resolvedPages.set(id, component);
		inflightLoads.delete(id);
		return component;
	});
	inflightLoads.set(id, promise);
	return promise;
}

/**
 * A memoized `React.lazy` wrapper around `preloadMarketingPage` — only used by `MarketingShell` for
 * a client-side navigation to a page that has not resolved yet (see the file's top comment: SSR and
 * the first client render never take this path, only a later in-app navigation between marketing
 * routes). Memoized per id so React sees a stable component identity across re-renders instead of a
 * new lazy-wrapped component (and a fresh Suspense trip) every time.
 */
export function lazyMarketingPage(id: MarketingPageId): LazyExoticComponent<ComponentType<any>> {
	const cached = lazyPages.get(id);
	if (cached) return cached;

	const LazyPage = lazy(() => preloadMarketingPage(id).then((component) => ({ default: component })));
	lazyPages.set(id, LazyPage);
	return LazyPage;
}
